// Minimal IndexedDB wrapper for offline QR scanning.
// No external dependency (ponytail: the platform already ships IndexedDB).
//
// Stores:
//  - registrations: keyPath 'key' = `${eventId}:${userId}`.
//      indexes: by_event (eventId), by_rfid (rfidKey = `${eventId}:${rfId}`)
//      Per-record storage keyed by userId gives O(1) offline lookups and lets us
//      mutate a single record's checkins without rewriting the whole 5k-row set.
//  - meta: keyPath 'eventId' -> { purposes, formSchema, syncedAt }.
//  - pendingScans: keyPath 'id' (autoIncrement) -> preserves scan order for sync.
//  - pendingRegistrations: keyPath 'id' (autoIncrement) -> on-spot registrations made offline.
//  - rejected: keyPath 'id' (autoIncrement) -> queued items the server refused, kept for staff to review.

const DB_NAME = 'offline-scan';
const DB_VERSION = 2;
const STORE_REG = 'registrations';
const STORE_META = 'meta';
const STORE_PENDING = 'pendingScans';
const STORE_PENDING_REG = 'pendingRegistrations';
const STORE_REJECTED = 'rejected';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_REG)) {
        const store = db.createObjectStore(STORE_REG, { keyPath: 'key' });
        store.createIndex('by_event', 'eventId', { unique: false });
        store.createIndex('by_rfid', 'rfidKey', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'eventId' });
      }
      [STORE_PENDING, STORE_PENDING_REG, STORE_REJECTED].forEach((name) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id', autoIncrement: true });
        }
      });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(db, store, mode) {
  return db.transaction(store, mode).objectStore(store);
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function txDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

const regKey = (eventId, userId) => `${eventId}:${userId}`;
const rfidKey = (eventId, rfId) => `${eventId}:${rfId}`;

function toStoredRegistration(eventId, dto) {
  const rfId = dto.rfId || (dto.formData && dto.formData.rfId) || null;
  // Keep the full DTO so the offline registrations table can render every column
  // (registeredAt, source, status, …), then layer on our index/lookup fields.
  return {
    ...dto,
    key: regKey(eventId, dto.userId),
    eventId,
    userId: dto.userId,
    rfId,
    rfidKey: rfId ? rfidKey(eventId, rfId) : undefined,
    formData: dto.formData || {},
    checkins: dto.checkins || {},
    multipleCheckins: dto.multipleCheckins || {},
    qrCodeBase64: dto.qrCodeBase64 || null,
    status: dto.status || null,
  };
}

/** All cached registrations for an event (used for offline filtering/paging). */
export async function getAllEventRegistrations(eventId) {
  const db = await openDb();
  const idx = tx(db, STORE_REG, 'readonly').index('by_event');
  const all = await reqToPromise(idx.getAll(IDBKeyRange.only(eventId)));
  return all || [];
}

/** Bulk upsert registrations for an event (used by cache priming). */
export async function bulkPutRegistrations(eventId, dtos = []) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  const store = transaction.objectStore(STORE_REG);
  for (const dto of dtos) {
    if (!dto || !dto.userId) continue;
    store.put(toStoredRegistration(eventId, dto));
  }
  await txDone(transaction);
}

/**
 * Upsert server rows without losing scans this device recorded but has not synced yet:
 * a purpose checked in locally stays checked in until the server row includes it.
 */
export async function mergeRegistrations(eventId, dtos = []) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  const store = transaction.objectStore(STORE_REG);
  for (const dto of dtos) {
    if (!dto || !dto.userId) continue;
    const request = store.get(regKey(eventId, dto.userId));
    request.onsuccess = () => {
      const existing = request.result;
      const checkins = { ...(existing?.checkins || {}), ...(dto.checkins || {}) };
      store.put(toStoredRegistration(eventId, { ...existing, ...dto, checkins, offlinePending: false }));
    };
  }
  await txDone(transaction);
}

/** Drop cached rows the server no longer returns (deleted registrations), but never ones still waiting to upload. */
export async function pruneEventRegistrations(eventId, keepUserIds) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  const store = transaction.objectStore(STORE_REG);
  const rows = await reqToPromise(store.index('by_event').getAll(IDBKeyRange.only(eventId)));
  const keep = new Set([...keepUserIds].map((userId) => regKey(eventId, userId)));
  for (const row of rows) {
    if (!keep.has(row.key) && !row.offlinePending) store.delete(row.key);
  }
  await txDone(transaction);
}

export async function deleteCachedRegistration(eventId, userId) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  transaction.objectStore(STORE_REG).delete(regKey(eventId, userId));
  await txDone(transaction);
}

/** Replace the whole cache for an event, then upsert fresh records. */
export async function replaceEventRegistrations(eventId, dtos = []) {
  await clearEventRegistrations(eventId);
  await bulkPutRegistrations(eventId, dtos);
}

export async function clearEventRegistrations(eventId) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  const store = transaction.objectStore(STORE_REG);
  const index = store.index('by_event');
  const keysReq = index.getAllKeys(IDBKeyRange.only(eventId));
  const keys = await reqToPromise(keysReq);
  for (const k of keys) store.delete(k);
  await txDone(transaction);
}

/** Look up a cached registration by userId first, then by rfId (mirrors backend OrRfid). */
export async function getRegistration(eventId, code) {
  const db = await openDb();
  const store = tx(db, STORE_REG, 'readonly');
  const byId = await reqToPromise(store.get(regKey(eventId, code)));
  if (byId) return byId;
  const store2 = tx(db, STORE_REG, 'readonly');
  const idx = store2.index('by_rfid');
  const byRfid = await reqToPromise(idx.get(rfidKey(eventId, code)));
  return byRfid || null;
}

export async function putRegistration(stored) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  transaction.objectStore(STORE_REG).put(stored);
  await txDone(transaction);
}

/** Patch a single cached registration's checkins map (keeps offline dedup consistent). */
export async function patchRegistrationCheckins(eventId, userId, checkins) {
  const db = await openDb();
  const transaction = db.transaction(STORE_REG, 'readwrite');
  const store = transaction.objectStore(STORE_REG);
  const existing = await reqToPromise(store.get(regKey(eventId, userId)));
  if (existing) {
    existing.checkins = { ...(existing.checkins || {}), ...(checkins || {}) };
    store.put(existing);
  }
  await txDone(transaction);
}

export async function countEventRegistrations(eventId) {
  const db = await openDb();
  const idx = tx(db, STORE_REG, 'readonly').index('by_event');
  return reqToPromise(idx.count(IDBKeyRange.only(eventId)));
}

export async function setMeta(eventId, meta) {
  const db = await openDb();
  const transaction = db.transaction(STORE_META, 'readwrite');
  transaction.objectStore(STORE_META).put({ eventId, ...meta });
  await txDone(transaction);
}

export async function getMeta(eventId) {
  const db = await openDb();
  return reqToPromise(tx(db, STORE_META, 'readonly').get(eventId));
}

/** Enqueue a pending scan; returns the assigned autoIncrement id (preserves order). */
export async function enqueuePendingScan(scan) {
  const db = await openDb();
  const transaction = db.transaction(STORE_PENDING, 'readwrite');
  const store = transaction.objectStore(STORE_PENDING);
  const id = await reqToPromise(store.add({
    ...scan,
    status: 'pending',
    attempts: 0,
    createdAt: scan.createdAt || new Date().toISOString(),
  }));
  await txDone(transaction);
  return id;
}

/** All pending scans in insertion order (id asc). */
export async function listPendingScans(eventId = null) {
  const db = await openDb();
  const all = await reqToPromise(tx(db, STORE_PENDING, 'readonly').getAll());
  const sorted = all.sort((a, b) => a.id - b.id);
  return eventId ? sorted.filter((s) => s.eventId === eventId) : sorted;
}

export async function updatePendingScan(scan) {
  const db = await openDb();
  const transaction = db.transaction(STORE_PENDING, 'readwrite');
  transaction.objectStore(STORE_PENDING).put(scan);
  await txDone(transaction);
}

export async function deletePendingScan(id) {
  const db = await openDb();
  const transaction = db.transaction(STORE_PENDING, 'readwrite');
  transaction.objectStore(STORE_PENDING).delete(id);
  await txDone(transaction);
}

export async function countPendingScans(eventId = null) {
  const scans = await listPendingScans(eventId);
  return scans.filter((s) => s.status !== 'synced').length;
}

async function addTo(storeName, value) {
  const db = await openDb();
  const transaction = db.transaction(storeName, 'readwrite');
  const id = await reqToPromise(transaction.objectStore(storeName).add(value));
  await txDone(transaction);
  return id;
}

async function listFrom(storeName, eventId) {
  const db = await openDb();
  const all = await reqToPromise(tx(db, storeName, 'readonly').getAll());
  const sorted = (all || []).sort((a, b) => a.id - b.id);
  return eventId ? sorted.filter((item) => item.eventId === eventId) : sorted;
}

async function deleteFrom(storeName, id) {
  const db = await openDb();
  const transaction = db.transaction(storeName, 'readwrite');
  transaction.objectStore(storeName).delete(id);
  await txDone(transaction);
}

export const enqueuePendingRegistration = (item) => addTo(STORE_PENDING_REG, { ...item, createdAt: new Date().toISOString() });
export const listPendingRegistrations = (eventId = null) => listFrom(STORE_PENDING_REG, eventId);
export const deletePendingRegistration = (id) => deleteFrom(STORE_PENDING_REG, id);

/** type: 'scan' | 'registration'; error: the server's message. */
export const addRejected = (item) => addTo(STORE_REJECTED, { ...item, rejectedAt: new Date().toISOString() });
export const listRejected = (eventId = null) => listFrom(STORE_REJECTED, eventId);
export const deleteRejected = (id) => deleteFrom(STORE_REJECTED, id);

export const __internal = { regKey, rfidKey, toStoredRegistration };
